import { useEffect } from 'react';

const SITE_NAME = 'FUDChat';
const SITE_URL = 'https://fudchat.com';
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;
const DEFAULT_DESC =
  'FUDChat is an independent AI-powered campus assistant that helps you explore Federal University Dutse (FUD) — faculties, departments, facilities, amenities, postgraduate programmes, research centres, SCEDL, notices and updates — with instant answers. FUDChat is not the university\'s official website or portal; the official site is https://fud.edu.ng.';
const TITLE_SUFFIX = ' | FUDChat';
const MAX_TITLE_LENGTH = 60;

const stripSiteSuffix = (title) => {
  const raw = String(title || '').trim();
  const stripped = raw
    .replace(/\s*[|—–-]\s*FUDChat\s*$/i, '')
    .replace(/\s+FUDChat\s*$/i, '')
    .trim();
  return stripped || raw;
};

const formatTitle = (title) => {
  const base = stripSiteSuffix(title) || 'Federal University Dutse AI Campus Assistant';
  const suffixLength = Array.from(TITLE_SUFFIX).length;
  const baseLength = MAX_TITLE_LENGTH - suffixLength;
  const characters = Array.from(base);
  const conciseBase = characters.length > baseLength
    ? `${characters.slice(0, baseLength - 1).join('').trimEnd()}…`
    : base;
  return `${conciseBase}${TITLE_SUFFIX}`;
};

/** Normalise a value into an absolute URL where appropriate. */
const toAbsolute = (value) => {
  if (!value) return null;
  if (value.startsWith('http')) return value;
  if (value.startsWith('/')) return `${SITE_URL}${value}`;
  return `${SITE_URL}/${value}`;
};

const toCanonical = (value) => {
  const absolute = toAbsolute(value);
  if (!absolute) return SITE_URL;
  const canonical = new URL(absolute);
  canonical.search = '';
  canonical.hash = '';
  if (canonical.pathname !== '/') {
    canonical.pathname = canonical.pathname.replace(/\/+$/, '');
  }
  return canonical.toString();
};

/** Create or update a <meta> tag. */
const upsertMeta = (selector, attribute, content, isProperty = false) => {
  if (content === undefined || content === null || content === '') {
    const existing = document.querySelector(selector);
    if (existing) existing.remove();
    return;
  }
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(isProperty ? 'property' : 'name', attribute);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

/** Create or update a <link> tag. */
const upsertLink = (rel, href, extra = {}) => {
  const selectorParts = [`link[rel="${rel}"]`];
  if (extra.hreflang) selectorParts.push(`[hreflang="${extra.hreflang}"]`);
  if (extra.type) selectorParts.push(`[type="${extra.type}"]`);
  const selector = selectorParts.join('');
  let link = document.querySelector(selector);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    if (extra.hreflang) link.setAttribute('hreflang', extra.hreflang);
    if (extra.type) link.setAttribute('type', extra.type);
    if (extra.title) link.setAttribute('title', extra.title);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

/**
 * SEO component — updates document head meta tags, canonical URL, Open Graph,
 * Twitter cards and JSON-LD structured data for maximum search engine, social
 * media and AI/LLM discoverability.
 *
 * @param {object} props
 * @param {string} props.title       Page title (site name is appended).
 * @param {string} props.description Meta description.
 * @param {string} props.image       Social share image (path or absolute URL).
 * @param {string} props.url         Canonical URL (defaults to current path).
 * @param {string} props.type        Open Graph type ('website' | 'article' | ...).
 * @param {string} props.robots      Robots directive.
 * @param {Array}  props.jsonLd      One or more JSON-LD schema objects.
 * @param {Array}  props.breadcrumbs Optional breadcrumb trail; when present a
 *                                   BreadcrumbList schema is generated.
 * @param {object} props.article     Article metadata (published/modified/author).
 */
const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  robots = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  jsonLd,
  breadcrumbs,
  article,
}) => {
  useEffect(() => {
    const fullTitle = formatTitle(title);
    const metaDescription = description || DEFAULT_DESC;
    const canonicalUrl = toCanonical(url || (typeof window !== 'undefined' ? window.location.pathname : '/'));
    const socialImage = toAbsolute(image) || DEFAULT_IMAGE;

    document.title = fullTitle;

    // Core meta
    upsertMeta('meta[name="description"]', 'description', metaDescription);
    upsertMeta('meta[name="robots"]', 'robots', robots);
    upsertMeta('meta[name="googlebot"]', 'googlebot', robots);
    upsertMeta('meta[name="bingbot"]', 'bingbot', robots);
    upsertMeta('meta[name="author"]', 'author', 'Nura Yusif (Mallam Nurr); Fatima Musa (Batul)');
    upsertMeta('meta[name="creator"]', 'creator', 'Nura Yusif (Mallam Nurr); Fatima Musa (Batul)');
    upsertMeta('meta[name="publisher"]', 'publisher', 'Grownex Hub');
    upsertMeta('meta[name="application-name"]', 'application-name', SITE_NAME);
    upsertMeta('meta[name="apple-mobile-web-app-title"]', 'apple-mobile-web-app-title', SITE_NAME);
    upsertMeta('meta[name="theme-color"]', 'theme-color', '#0F6A3D');
    upsertMeta('meta[name="color-scheme"]', 'color-scheme', 'light dark');
    upsertMeta('meta[name="referrer"]', 'referrer', 'strict-origin-when-cross-origin');
    upsertMeta('meta[name="revisit-after"]', 'revisit-after', '7 days');
    upsertMeta('meta[name="subject"]', 'subject', 'Federal University Dutse academic information, campus resources, AI assistant, student services');
    upsertMeta('meta[name="classification"]', 'classification', 'Education, Higher Education, University, Nigeria, Campus Services');

    // Open Graph
    upsertMeta('meta[property="og:title"]', 'og:title', fullTitle, true);
    upsertMeta('meta[property="og:description"]', 'og:description', metaDescription, true);
    upsertMeta('meta[property="og:url"]', 'og:url', canonicalUrl, true);
    upsertMeta('meta[property="og:image"]', 'og:image', socialImage, true);
    upsertMeta('meta[property="og:image:secure_url"]', 'og:image:secure_url', socialImage, true);
    upsertMeta('meta[property="og:image:alt"]', 'og:image:alt', title || `${SITE_NAME} Logo`, true);
    upsertMeta('meta[property="og:site_name"]', 'og:site_name', SITE_NAME, true);
    upsertMeta('meta[property="og:locale"]', 'og:locale', 'en_NG', true);
    upsertMeta('meta[property="og:locale:alternate"]', 'og:locale:alternate', 'en_US', true);
    upsertMeta('meta[property="og:type"]', 'og:type', type, true);

    upsertMeta(
      'meta[property="article:published_time"]',
      'article:published_time',
      type === 'article' ? article?.publishedTime : null,
      true
    );
    upsertMeta(
      'meta[property="article:modified_time"]',
      'article:modified_time',
      type === 'article' ? article?.modifiedTime : null,
      true
    );
    upsertMeta(
      'meta[property="article:author"]',
      'article:author',
      type === 'article' ? article?.author : null,
      true
    );

    // Twitter / X
    upsertMeta('meta[name="twitter:card"]', 'twitter:card', 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', 'twitter:title', fullTitle);
    upsertMeta('meta[name="twitter:description"]', 'twitter:description', metaDescription);
    upsertMeta('meta[name="twitter:image"]', 'twitter:image', socialImage);
    upsertMeta('meta[name="twitter:image:alt"]', 'twitter:image:alt', title || `${SITE_NAME} Logo`);
    upsertMeta('meta[name="twitter:site"]', 'twitter:site', '@fudchat01');
    upsertMeta('meta[name="twitter:creator"]', 'twitter:creator', '@fudchat01');

    // og:image dimensions must describe the actual bytes at DEFAULT_IMAGE.
    // Declaring 1200x630 for a 512x512 square makes the platforms letterbox the
    // card and wastes the first fetch on a crop. Keep these in step with
    // DEFAULT_IMAGE — and with OG_IMAGE_WIDTH/HEIGHT in scripts/prerender.mjs,
    // which writes the same tags at build time — so client-side SEO updates do
    // not revert them.
    const defaultImageDimensions = socialImage === DEFAULT_IMAGE ? '512' : null;
    upsertMeta('meta[property="og:image:width"]', 'og:image:width', defaultImageDimensions, true);
    upsertMeta('meta[property="og:image:height"]', 'og:image:height', defaultImageDimensions, true);

    // Canonical + alternates
    upsertLink('canonical', canonicalUrl);
    upsertLink('alternate', canonicalUrl, { hreflang: 'en' });
    upsertLink('alternate', canonicalUrl, { hreflang: 'x-default' });

    // JSON-LD structured data
    const scriptId = 'dynamic-seo-ld';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();

    const schemas = [];
    if (Array.isArray(jsonLd)) {
      schemas.push(...jsonLd.filter(Boolean));
    } else if (jsonLd) {
      schemas.push(jsonLd);
    }

    if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          name: crumb.name,
          item: toAbsolute(crumb.url || '/'),
        })),
      });
    }

    const prerenderedSchema = document.getElementById('prerendered-ld');
    if (
      prerenderedSchema &&
      (schemas.length > 0 || prerenderedSchema.getAttribute('data-canonical') !== canonicalUrl)
    ) {
      prerenderedSchema.remove();
    }

    if (schemas.length > 0) {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('id', scriptId);
      script.innerHTML = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
      document.head.appendChild(script);
    }

    return () => {
      // Keep tags until the next page updates them (avoids flicker during SPA nav).
    };
  }, [title, description, image, url, type, robots, jsonLd, breadcrumbs, article]);

  return null;
};

/** Remove the admin-only robots directives when leaving the admin area. */
export const resetRobotsMeta = () => {
  ['meta[name="robots"]', 'meta[name="googlebot"]', 'meta[name="bingbot"]'].forEach((selector) => {
    document.querySelectorAll(selector).forEach((element) => element.remove());
  });
};

export default SEO;
export { SITE_URL, SITE_NAME, DEFAULT_IMAGE };
